import type { ContentItem } from "../layout/documentLayout";
import {
  classifySignatureAnswer,
  DIGITAL_SIGNATURE_CONSENT_MARKER,
  PATIENT_NOT_SIGNING_OTHER,
  patientNotSigningSelection,
  type PatientNotSigningReason,
  type SignatureValue,
} from "./signatureValue";

// Pure draft logic of the signature editor (employeeSignature, patientSignature,
// customSignature): which controls a question offers, the draft read from the stored answer, the
// control transitions and the answer a draft saves. The answers are exactly what the classic modal
// (editPatientDocModalCtrl.js) and mobile (RequiredDocSignature.tsx) store:
// - signing: the pad's data URL ("" once cleared);
// - employee / custom not signing: the typed name;
// - patient not signing: the reason, or the "Other" free text;
// - verbal consent: DIGITAL_SIGNATURE_CONSENT_MARKER. Offered only where the server honours the
//   marker: customSignature ("Signee gave verbal consent") and, under "Patient is not signing",
//   patientSignature ("Patient approves digital signature", as mobile offers it).

export type SignatureItemType = Extract<
  ContentItem["itemType"],
  "employeeSignature" | "patientSignature" | "customSignature"
>;

export function isSignatureItemType(
  itemType: ContentItem["itemType"]
): itemType is SignatureItemType {
  return (
    itemType === "employeeSignature" ||
    itemType === "patientSignature" ||
    itemType === "customSignature"
  );
}

export type SignatureMode = "sign" | "notSigning" | "consent";

export type PatientNotSigningChoice = PatientNotSigningReason | typeof PATIENT_NOT_SIGNING_OTHER;

export interface SignatureDraft {
  mode: SignatureMode;
  /** Patient not signing: the selected radio (null = none yet). */
  reason: PatientNotSigningChoice | null;
  /** Patient not signing: the "Other" free text. */
  otherText: string;
  /** Employee / custom not signing: the typed name. */
  notSigningName: string;
  /** The pad's content: a data URL, "" when empty or cleared. Kept while not signing. */
  signature: string;
}

export interface SignatureOptions {
  /** Where verbal consent is offered: its own checkbox (custom) or under not signing (patient). */
  consent: "none" | "standalone" | "underNotSigning";
  notSigning: boolean;
}

/**
 * Controls are gated by `allowUnsignedSignature` (classic modal and mobile). A stored not-signing
 * or consent answer still shows its controls when the flag is off, so it can be seen and changed
 * (customSignature never offers not signing).
 */
export function signatureOptions(
  question: Pick<ContentItem, "allowUnsignedSignature">,
  itemType: SignatureItemType,
  stored: unknown
): SignatureOptions {
  const kind = classifySignatureAnswer(stored).kind;
  const allowsUnsigned = question.allowUnsignedSignature === true;
  switch (itemType) {
    case "employeeSignature":
      return { consent: "none", notSigning: allowsUnsigned || kind === "notSigning" };
    case "patientSignature": {
      const notSigning = allowsUnsigned || kind === "notSigning" || kind === "consent";
      return { consent: notSigning ? "underNotSigning" : "none", notSigning };
    }
    case "customSignature":
      // Never "Not signing": mobile does not offer it and the PDF prints any text other than the
      // consent marker as an image URL
      return { consent: "standalone", notSigning: false };
  }
}

/**
 * A stored answer the editor cannot show as such (an employee consent marker, a custom signature's
 * text): it opens on an empty pad and is kept unless the reviewer signs. null = nothing to say.
 */
export function unsupportedStoredAnswerNote(
  itemType: SignatureItemType,
  kind: SignatureValue["kind"]
): string | null {
  if (itemType === "employeeSignature" && kind === "consent") {
    return "The stored answer is a verbal-consent marker, which employee signatures do not use. It is kept unless you sign.";
  }
  if (itemType === "customSignature" && kind === "notSigning") {
    return "The stored answer is text, not a signature, so the PDF cannot print it. It is kept unless you sign.";
  }
  return null;
}

const EMPTY_DRAFT: SignatureDraft = {
  mode: "sign",
  reason: null,
  otherText: "",
  notSigningName: "",
  signature: "",
};

/**
 * The draft a stored answer opens with. An employee signature holding the consent marker (never
 * written by any client) and a custom signature holding text open as an empty pad; Save stays
 * disabled until changed, so it is kept.
 */
export function initialSignatureDraft(
  itemType: SignatureItemType,
  stored: unknown
): SignatureDraft {
  const value = classifySignatureAnswer(stored);
  switch (value.kind) {
    case "empty":
      return EMPTY_DRAFT;
    case "image":
      return { ...EMPTY_DRAFT, signature: value.dataUrl };
    case "consent":
      return itemType === "employeeSignature" ? EMPTY_DRAFT : { ...EMPTY_DRAFT, mode: "consent" };
    case "notSigning": {
      // customSignature has no not-signing mode (see signatureOptions)
      if (itemType === "customSignature") {
        return EMPTY_DRAFT;
      }
      if (itemType === "patientSignature") {
        const selection = patientNotSigningSelection(value.text);
        return {
          ...EMPTY_DRAFT,
          mode: "notSigning",
          reason: selection.reason,
          otherText: selection.otherText,
        };
      }
      return { ...EMPTY_DRAFT, mode: "notSigning", notSigningName: value.text };
    }
  }
}

/** The not-signing checkbox ("Not signing" / "Patient is not signing"). */
export function setNotSigning(draft: SignatureDraft, isNotSigning: boolean): SignatureDraft {
  return { ...draft, mode: isNotSigning ? "notSigning" : "sign" };
}

/**
 * The consent checkbox. Custom: checking drops the drawn signature (mobile), unchecking goes back
 * to an empty pad, so it saves "" (classic modal). Patient: unchecking goes back to the reasons
 * with none selected (mobile), so a reason must be picked before saving.
 */
export function setConsent(
  itemType: SignatureItemType,
  draft: SignatureDraft,
  hasConsent: boolean
): SignatureDraft {
  if (itemType === "patientSignature") {
    return hasConsent
      ? { ...draft, mode: "consent", reason: null, otherText: "" }
      : { ...draft, mode: "notSigning", reason: null, otherText: "" };
  }
  return { ...draft, mode: hasConsent ? "consent" : "sign", signature: "" };
}

/** The answer a draft saves; null while it is incomplete (no reason, empty Other text or name). */
export function signatureAnswerFromDraft(
  itemType: SignatureItemType,
  draft: SignatureDraft
): string | null {
  switch (draft.mode) {
    case "sign":
      return draft.signature;
    case "consent":
      return DIGITAL_SIGNATURE_CONSENT_MARKER;
    case "notSigning": {
      if (itemType !== "patientSignature") {
        return draft.notSigningName.trim() === "" ? null : draft.notSigningName;
      }
      if (draft.reason === null) {
        return null;
      }
      if (draft.reason === PATIENT_NOT_SIGNING_OTHER) {
        return draft.otherText.trim() === "" ? null : draft.otherText;
      }
      return draft.reason;
    }
  }
}

/** customSignature's name child (`columns[0][0]`, textShort); null when there is none. */
export function customSignatureNameQuestion(question: ContentItem): ContentItem | null {
  return question.columns?.[0]?.[0] ?? null;
}
